import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Skeleton } from 'primereact/skeleton';
import { sessionsApi } from 'api';
import SessionBox from 'components/userPanel/sessions/partials/SessionBox';
import { useSessionsData } from 'components/userPanel/sessions/Sessions';
import { usePair } from 'contexts/pairContext';
import { usePanel } from 'contexts/panelContext';
import { useGlobal } from 'contexts/globalContext';
import { Button } from 'components/_new/Button';
import { DialogAction } from 'components/_new/Dialog';
import { Dropdown } from 'components/_new/Dropdown';
import { Controller, useForm } from 'react-hook-form';
import { SessionTypeEnum } from 'types/Session';
import SubPageTitle from '../../_common/panel/SubPageTitle';
import { RouterUrlParams } from '../../../App';
import PairPartnerInfo from '../components/PairPartnerInfo';

const SessionsList = () => {
	const { t } = useTranslation();
	const params = useParams() as RouterUrlParams;
	const { sessionId } = params;
	const { panelType, currentProgramMembership } = usePanel();
	const { toastRef } = useGlobal();
	const {
		pair: { id: pairId },
	} = usePair();

	const { sessionsData, sessiosRefetch, isLoading } = useSessionsData();
	const currentDate = new Date();
	const firstUnallocated = sessionsData.find((session) => session.date === null);
	let currentSession =
		sessionsData.find((element) => element.date && new Date(element.date).getTime() > currentDate.getTime()) ||
		firstUnallocated ||
		sessionsData[sessionsData.length - 1];
	if (firstUnallocated) {
		const oneBefore = sessionsData.find((session) => session.sessionNumber === firstUnallocated.sessionNumber - 1);
		if (oneBefore && oneBefore.date < currentSession.date) {
			currentSession = oneBefore;
		}
	}

	const [addNewSessionDialog, setAddNewSessionDialog] = useState(false);
	const { control, handleSubmit } = useForm({
		defaultValues: {
			newSessionAfter: sessionsData.length,
		},
	});

	const nextSessionDate =
		currentSession && sessionsData.find((session) => session.sessionNumber === currentSession.sessionNumber + 1);
	const { mutate: createSession } = useMutation(
		(afterSessionNumber: number) =>
			sessionsApi.createSession(pairId, currentProgramMembership.id, afterSessionNumber),
		{
			onSettled: (data) => {
				if (data) {
					sessiosRefetch();
					toastRef?.current?.show({
						severity: 'success',
						life: 3000,
						summary: t('misc.success'),
						detail: t('userPanel.sessions.createdSuccess'),
					});

					setTimeout(() => {
						const newSessionBoxId = `session-${data.id}`;
						const element = document.getElementById(newSessionBoxId)!;
						element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
						setAddNewSessionDialog(false);
					}, 500);
				}
			},
		}
	);
	const handleAddNewSessionFormSubmit = handleSubmit(({ newSessionAfter }) => createSession(newSessionAfter));

	const computedTitle = `${t('userPanel.sessions.header', {
		type: panelType === 'mentor' ? 'Mentee' : t('misc.withMentor'),
	})}`;

	if (isLoading) {
		return (
			<>
				<SubPageTitle
					title={
						<div className="flex flex-column">
							<Skeleton width="200px" height="2rem" className="mb-2" />
							<div className="flex flex-row align-items-center gap-2">
								<Skeleton width="150px" height="1.5rem" />
								<Skeleton width="20px" height="20px" />
							</div>
						</div>
					}
					additionalStyles={{
						padding: '0rem !important',
					}}
					right={<Skeleton width="150px" height="2.5rem" borderRadius="8px" />}
				/>
				<div className="flex flex-column gap-2 pb-3">
					{[1, 2, 3, 4, 5, 6].map((item) => (
						<div
							key={item}
							className="p-3 border-round surface-card"
							style={{ minHeight: '7rem', borderLeft: '4px solid #e0e0e0' }}
						>
							<div className="flex w-full justify-content-between">
								<div className="flex flex-column gap-3 flex-1">
									<div className="flex flex-row gap-3 align-items-center">
										<Skeleton width="80px" height="1.5rem" />
										<Skeleton width="100px" height="1.5rem" borderRadius="16px" />
									</div>
									<Skeleton width="70%" height="1.2rem" />
								</div>
								<div className="flex flex-row align-items-center gap-2">
									<Skeleton width="120px" height="2.5rem" borderRadius="20px" />
									<Skeleton width="40px" height="40px" borderRadius="50%" />
								</div>
							</div>
						</div>
					))}
				</div>
			</>
		);
	}

	const lastSessionWithDate = [...sessionsData].reverse().find((session) => session.date);

	return (
		<>
			<DialogAction
				visible={addNewSessionDialog}
				onHide={() => {
					setAddNewSessionDialog(false);
				}}
				title={t('userPanel.sessions.addSession')}
				actions={[
					{
						key: 'confirm',
						label: t('actions.add'),
						form: 'addNewSessionFor',
						submit: true,
					},
				]}
				size="sm"
			>
				<form id="addNewSessionFor" onSubmit={handleAddNewSessionFormSubmit}>
					<Controller
						control={control}
						name="newSessionAfter"
						render={({ field }) => (
							<Dropdown
								inputRef={field.ref}
								value={field.value}
								onChange={({ value }) => {
									field.onChange(value);
								}}
								withEmpty
								emptyValue={0}
								emptyLabel={t('userPanel.sessionsNew.addNewSessionFirst')}
								emptyClassName={currentSession ? 'text-error' : undefined}
								options={sessionsData.map(({ sessionNumber, sessionType }) => {
									const sessionTypeStr = (() => {
										switch (sessionType) {
											case SessionTypeEnum.FIRST:
												return t('userPanel.sessions.first.title');
											case SessionTypeEnum.SECOND:
												return t('userPanel.sessions.second.title');
											case SessionTypeEnum.MIDDLE:
												return t('userPanel.sessions.middle.title');
											case SessionTypeEnum.PENULTIMATE:
												return t('userPanel.sessions.penultimate.title');
											case SessionTypeEnum.LAST:
												return t('userPanel.sessions.last.title');
											default:
												return null;
										}
									})();

									// const highlight = sessionNumber < (currentSession?.sessionNumber || 0) - 1;

									return {
										label: t('userPanel.sessionsNew.addNewSessionAfter', {
											session: `${sessionNumber}${sessionTypeStr ? ` (${sessionTypeStr})` : ''}`,
										}),
										value: sessionNumber,
										// className: highlight ? 'text-error' : undefined,
									};
								})}
							/>
						)}
					/>
				</form>
			</DialogAction>
			<Helmet title={computedTitle} />
			<SubPageTitle
				title={
					<div className="flex flex-column">
						{computedTitle}
						<PairPartnerInfo />
					</div>
				}
				additionalStyles={{
					padding: '0rem !important',
				}}
				right={
					<Button
						label={t('userPanel.sessions.addSession')}
						variant="primary-text"
						icon="plus"
						iconStyle="solid"
						onClick={() => setAddNewSessionDialog(true)}
						className="p-button-link cursor-pointer"
						disabled={!!sessionId}
					/>
				}
			/>
			<div className="flex flex-column gap-2 pb-3">
				{sessionsData.map((session, index) => {
					const previousSession = index > 0 ? sessionsData[index - 1] : undefined;
					const nextSession = index < sessionsData.length - 1 ? sessionsData[index + 1] : undefined;
					const canDisplayTasks = nextSessionDate
						? session.sessionNumber < nextSessionDate.sessionNumber
						: true;

					const isFirst = index === 0;
					const isLast = index === sessionsData.length - 1;

					const canAddBefore =
						session.date === null && session.sessionNumber > (lastSessionWithDate?.sessionNumber || 0);
					const canAddAfter = session.sessionNumber >= (lastSessionWithDate?.sessionNumber || 0);

					return (
						<SessionBox
							id={`session-${session.id}`}
							key={session.id}
							previousSession={previousSession}
							nextSession={nextSession}
							session={session}
							canDisplayTasks={canDisplayTasks}
							showSetDateButton={session.id === firstUnallocated?.id}
							meta={{ isFirst, isLast, canAddBefore, canAddAfter }}
							addSession={(direction) => {
								createSession(
									direction === 'up' ? previousSession?.sessionNumber || 0 : session.sessionNumber
								);
							}}
						/>
					);
				})}
			</div>
		</>
	);
};

export default SessionsList;
